import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Eye, Pencil, AlertTriangle, X, Building2 } from 'lucide-react';
import { fetchAuditLogs } from '../services/systemAuditLogService';
import { listOrganizations, upsertOrganization, createOrganizationOnServer, updateOrganizationOnServer, updateOrganizationStatusOnServer } from '../services/organizationService';
import { getSystemDb } from '@firesquirrel/local-db';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn, DataGridFilter, DataGridRowAction, GridTemplate } from '@ui-admin/components/DataGrid';
import { InlineEditableField } from '@ui-admin/components/InlineEditableField';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import ConfirmDeleteModal from '../components/ui/ConfirmDeleteModal';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';

function toStr(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return (v as any).name ?? (v as any).code ?? JSON.stringify(v);
  return String(v);
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-600',
  suspended: 'bg-red-100 text-red-700',
};

const ORG_TYPE_LABELS: Record<string, string> = {
  county: 'County', state: 'State', authority: 'Fire Authority',
  district: 'District', municipal: 'Municipal', regional: 'Regional',
};

function getOrgTypeCode(org: any): string {
  return toStr(org?.type);
}

function getOrgTypeLabel(org: any): string {
  const code = getOrgTypeCode(org);
  return ORG_TYPE_LABELS[code] || code || '—';
}

function getDefaultBillingCode(org: any): string {
  return toStr(org?.defaultBillingEntity);
}

function getDefaultBillingLabel(org: any): string {
  const code = getDefaultBillingCode(org);
  if (code === 'organization') return 'Organization-level';
  if (code === 'department') return 'Per Department';
  return code || '—';
}

const COLUMNS: DataGridColumn<any>[] = [
  {
    id: 'name', header: 'Organization', accessorKey: 'name', sortable: true, filterable: true, minWidth: 200,
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex items-start gap-2.5">
          <div className="shrink-0 mt-0.5 h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <Building2 className="h-4 w-4 text-blue-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{row.name}</p>
            <p className="text-xs text-gray-400 font-mono truncate">{row.slug || '—'}</p>
          </div>
        </div>
      );
    },
  },
  {
    id: 'type', header: 'Type', accessorFn: (row: any) => toStr(row.type), sortable: true, filterable: true, width: 130,
    cell: (info) => { const v = toStr(info.getValue()); return <span className="text-sm text-gray-600">{ORG_TYPE_LABELS[v] || v || '—'}</span>; },
  },
  {
    id: 'status', header: 'Status', accessorFn: (row: any) => toStr(row.status), sortable: true, filterable: true, width: 110,
    cell: (info) => {
      const v = toStr(info.getValue());
      return v ? <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full capitalize ${STATUS_COLORS[v] || 'bg-gray-100 text-gray-600'}`}>{v}</span> : <span className="text-gray-400">—</span>;
    },
  },
  {
    id: 'memberCount', header: 'Departments', accessorFn: (row: any) => row.memberTenantIds?.length ?? 0, sortable: true, width: 120,
    cell: (info) => { const n = info.getValue() as number; return <span className="text-sm text-gray-600">{n > 0 ? n : <span className="text-gray-300">—</span>}</span>; },
  },
  {
    id: 'defaultBillingEntity', header: 'Billing', accessorFn: (row: any) => toStr(row.defaultBillingEntity), sortable: true, width: 130,
    cell: (info) => { const v = toStr(info.getValue()); return <span className="text-sm text-gray-500">{v === 'organization' ? 'Org-level' : v === 'department' ? 'Per dept.' : v || '—'}</span>; },
  },
  {
    id: 'createdAt', header: 'Created', accessorKey: 'createdAt', sortable: true, width: 110, hidden: true,
    cell: (info) => { const v = info.getValue() as string; return <span className="text-sm text-gray-500">{v ? new Date(v).toLocaleDateString() : '—'}</span>; },
  },
];

const FILTERS: DataGridFilter[] = [
  { id: 'type', label: 'Type', type: 'select', options: Object.entries(ORG_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })) },
  { id: 'status', label: 'Status', type: 'status', options: [{ value: 'active', label: 'Active', color: 'green' }, { value: 'inactive', label: 'Inactive', color: 'gray' }, { value: 'suspended', label: 'Suspended', color: 'red' }] },
];

const SEED_TEMPLATES: GridTemplate[] = [
  { _id: 'orgs-default', gridType: 'organizations', name: 'Default View', description: 'All organizations sorted by name.', state: { columnVisibility: {}, columnOrder: [], sorting: [{ id: 'name', desc: false }], filterValues: {}, pageSize: 25, searchText: '' }, scope: 'system', createdBy: { userId: '507f1f77bcf86cd799439001', displayName: 'System' }, isDefault: true, isPinned: false, usageCount: 0, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { _id: 'orgs-active', gridType: 'organizations', name: 'Active Only', description: 'Active organizations only.', state: { columnVisibility: {}, columnOrder: [], sorting: [{ id: 'name', desc: false }], filterValues: { status: ['active'] }, pageSize: 25, searchText: '' }, scope: 'system', createdBy: { userId: '507f1f77bcf86cd799439001', displayName: 'System' }, isDefault: false, isPinned: false, usageCount: 0, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
];

// ============================================================
// OrganizationPanel — slide-out panel
// ============================================================

function OrganizationPanel({ org, onSave }: { org: any; onSave: (fieldKey: string, value: unknown) => Promise<void> }) {
  const { theme } = useTheme();
  const [panelTab, setPanelTab] = useState<'general' | 'departments' | 'audit'>('general');

  return (
    <div className="flex flex-col h-full" style={{ background: theme.cardBg, color: theme.textPrimary }}>
      <div className="flex border-b shrink-0 overflow-x-auto" style={{ borderColor: theme.cardBorder }}>
        {(['general', 'departments', 'audit'] as const).map((t) => (
          <button key={t} onClick={() => setPanelTab(t)}
            className="px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors capitalize"
            style={{ borderBottomColor: panelTab === t ? theme.accent : 'transparent', color: panelTab === t ? theme.accentText : theme.textSecondary }}>
            {t === 'departments' ? 'Departments' : t === 'audit' ? 'Audit Log' : 'General'}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {panelTab === 'general' && (
          <div className="space-y-1">
            <InlineEditableField label="Name" value={org.name} config={{ fieldKey: 'name', editType: 'text' }} onSave={onSave} />
            <div>
              <dt className="text-xs font-medium mb-0.5" style={{ color: theme.textSecondary }}>Slug</dt>
              <dd className="text-xs font-mono" style={{ color: theme.textPrimary }}>{org.slug || '—'}</dd>
            </div>
            <InlineEditableField label="Type" value={getOrgTypeLabel(org)} editValue={getOrgTypeCode(org)} config={{ fieldKey: 'type', editType: 'select', options: Object.entries(ORG_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })) }} onSave={onSave} />
            <InlineEditableField label="Status" value={org.status} config={{ fieldKey: 'status', editType: 'select', options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'suspended', label: 'Suspended' }] }} onSave={onSave} />
            <InlineEditableField label="Default Billing" value={getDefaultBillingLabel(org)} editValue={getDefaultBillingCode(org)} config={{ fieldKey: 'defaultBillingEntity', editType: 'select', options: [{ value: 'department', label: 'Per Department' }, { value: 'organization', label: 'Organization-level' }] }} onSave={onSave} />
            <div>
              <dt className="text-xs font-medium mb-0.5" style={{ color: theme.textSecondary }}>Created</dt>
              <dd className="text-sm" style={{ color: theme.textPrimary }}>{org.createdAt ? new Date(org.createdAt).toLocaleDateString() : '—'}</dd>
            </div>
          </div>
        )}
        {panelTab === 'audit' && (
          <AuditLogTimeline
            documentId={org._id}
            collection="organizations"
            theme={theme}
            active={panelTab === 'audit'}
            fetchFn={fetchAuditLogs}
          />
        )}
        {panelTab === 'departments' && (
          <div>
            {(!org.memberTenantIds || org.memberTenantIds.length === 0) ? (
              <div className="text-center py-10 text-sm" style={{ color: theme.textSecondary }}>No member departments</div>
            ) : (
              <div className="space-y-2">
                {org.memberTenantIds.map((id: any) => (
                  <div key={String(id)} className="px-3 py-2 rounded-lg text-xs font-mono" style={{ background: theme.expandedBg, color: theme.textSecondary }}>{String(id)}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// CreateOrganizationModal
// ============================================================

function CreateOrganizationModal({ onClose, onCreated }: { onClose: () => void; onCreated: (org: any) => void }) {
  const [form, setForm] = useState({ name: '', slug: '', type: 'county', defaultBillingEntity: 'department' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const newOrg = {
        _id: crypto.randomUUID(),
        name: form.name,
        slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        type: form.type,
        status: 'active',
        defaultBillingEntity: form.defaultBillingEntity,
        memberTenantIds: [],
        adminUserId: null,
        createdAt: now,
        updatedAt: now,
      };
      await upsertOrganization(newOrg);
      onCreated(newOrg);
      const tempId = newOrg._id;
      createOrganizationOnServer(newOrg)
        .then(async (saved: any) => {
          await upsertOrganization(saved);
          if (saved._id !== tempId) {
            const db = await getSystemDb();
            const tempDoc = await db.organizations.findOne(tempId).exec();
            // Hard delete is correct here: the optimistic temp doc uses a
            // client-generated id the server never saw, so a soft-delete
            // tombstone would push a deletion for a nonexistent record.
            // eslint-disable-next-line no-restricted-syntax
            if (tempDoc) await (tempDoc as any).remove();
          }
        })
        .catch((err) => {
          console.warn('[OrganizationsPage] Failed to sync organization creation to server:', err);
        });
    } catch (err: any) {
      setError(err.message || 'Failed to create organization');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-900">New Organization</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" /><span>{error}</span>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              placeholder="Maricopa County Fire Authority" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug <span className="text-gray-400 font-normal">(auto-generated if empty)</span></label>
            <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500"
              placeholder="maricopa-county" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                {Object.entries(ORG_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing</label>
              <select value={form.defaultBillingEntity} onChange={(e) => setForm({ ...form, defaultBillingEntity: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="department">Per Department</option>
                <option value="organization">Org-level</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// OrganizationsPage
// ============================================================

export default function OrganizationsPage() {
  const [orgs, setOrgs] = useState<any[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<any | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState<any | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadOrgs = useCallback(() => {
    listOrganizations().then(setOrgs).catch(() => setOrgs([]));
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const handlePanelSave = useCallback(async (fieldKey: string, value: unknown) => {
    if (!selectedOrg) return;
    const updated = { ...selectedOrg, [fieldKey]: value };
    const prev = selectedOrg;
    setSelectedOrg(updated);
    setOrgs((os) => os.map((o) => o._id === updated._id ? updated : o));
    try {
      await upsertOrganization(updated);
      updateOrganizationOnServer(updated._id, updated).catch((err) => {
        console.warn('[OrganizationsPage] Failed to sync organization update to server:', err);
      });
    } catch (err: any) {
      setSelectedOrg(prev);
      setOrgs((os) => os.map((o) => o._id === prev._id ? prev : o));
    }
  }, [selectedOrg]);

  const handleSuspend = async () => {
    if (!suspendTarget) return;
    try {
      const updated = { ...suspendTarget, status: 'suspended' };
      await upsertOrganization(updated);
      setOrgs((os) => os.map((o) => o._id === updated._id ? updated : o));
      if (selectedOrg?._id === updated._id) setSelectedOrg(updated);
      setSuspendTarget(null);
      updateOrganizationStatusOnServer(suspendTarget._id, 'suspended').catch((err) => {
        console.warn('[OrganizationsPage] Failed to sync organization suspend to server:', err);
      });
    } catch (err: any) {
      setActionError(err.message || 'Failed to suspend organization');
      setSuspendTarget(null);
    }
  };

  const ROW_ACTIONS: DataGridRowAction<any>[] = [
    { id: 'view', label: 'View Details', icon: Eye, onClick: (row) => setSelectedOrg(row) },
    { id: 'edit', label: 'Edit', icon: Pencil, onClick: (row) => setSelectedOrg(row) },
    { id: 'suspend', label: 'Suspend', icon: Trash2, variant: 'danger' as const, onClick: (row) => setSuspendTarget(row), hidden: (row) => row.status === 'suspended' },
  ];

  return (
    <>
      {actionError && (
        <div className="mx-6 mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 shrink-0">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError(null)} className="shrink-0 text-red-400 hover:text-red-600 ml-2">✕</button>
        </div>
      )}

      <GridPageShell
        gridId="organizations"
        gridType="organizations"
        title="Organizations"
        subtitle="Counties, states, and fire authorities"
        columns={COLUMNS}
        data={orgs}
        getRowId={(o: any) => o._id}
        filters={FILTERS}
        rowActions={ROW_ACTIONS}
        onRowClick={(o: any) => setSelectedOrg(o)}
        activeRowId={selectedOrg?._id ?? null}
        onPanelClose={() => setSelectedOrg(null)}
        panelTitle={selectedOrg?.name}
        panelSubtitle={selectedOrg?.slug}
        panelDefaultWidth={480}
        panelContent={selectedOrg ? <OrganizationPanel org={selectedOrg} onSave={handlePanelSave} /> : null}
        seedTemplates={SEED_TEMPLATES}
        onRefresh={loadOrgs}
        emptyCTA={{ label: 'New Organization', onClick: () => setShowCreateModal(true) }}
        toolbarActions={
          <button onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            <Plus className="h-4 w-4" /> New Organization
          </button>
        }
      />

      {showCreateModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(org) => { setOrgs((os) => [...os, org]); setShowCreateModal(false); setSelectedOrg(org); }}
        />
      )}

      {suspendTarget && (
        <ConfirmDeleteModal
          title="Suspend Organization?"
          confirmValue={suspendTarget.name}
          confirmLabel="organization name"
          description={<>Suspend <strong>{suspendTarget.name}</strong>? This is a soft-delete action and can be reactivated later.</>}
          warning="Suspended organizations are hidden from default active views and users will lose normal access until reactivated."
          deleteButtonLabel="Suspend Organization"
          onConfirm={handleSuspend}
          onClose={() => setSuspendTarget(null)}
        />
      )}
    </>
  );
}
